import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IUserCreateDTO, IUserDetailsDTO, IUserUpdateDTO } from '../../../api/users.dao';
import { MINIMAL_SEARCH_LENGTH, SHORT_PLAIN_TEXT_LENGTH } from '../../../constants/common.constants';
import { EMAIL_VALIDATION_REG_EXP } from '../../../constants/regex.constants';
import { getRandomPassword } from '../../../infrastructure/utils/common.utils';
import { IAccountDetailsDTO, IAccountsDAO } from '../../../api/accounts.dao';
import { IRolesDAO, IRolesDTO } from '../../../api/roles.dao';
import { COMMON_ITEMS_PER_PAGE } from '../../../constants/pagination.contants';
import { IAccountGroupDTO, IAccountGroupsDAO } from '../../../api/account-groups.dao';

const _assignedAccountIds = Symbol('initialAssignedAccountIds');
const _assignedRoleLinkNames = Symbol('initialAssignedRoles');
const _assignedAccountGroupIds = Symbol('initialAssignedAccountGroupIds');

class UserFormDataVM {
    public defaultValues = {
        firstName: '',
        lastName: '',
        email: '',
        tempPassword: '',
        isSuperuser: false,
        isOpenExchangeStaffMember: false,
        isAllAccounts: false,
        accounts: [],
        roles: [],
        accountGroups: [],
        confirmationEmailPasswordTrigger: false,
        active: true,
        lockedSystemUser: false
    };

    constructor(
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly rolesDAO: IRolesDAO = IoC.Container.get(IRolesDAO),
        @IoC.Inject private readonly accountsGroupsDAO: IAccountGroupsDAO = IoC.Container.get(IAccountGroupsDAO)
    ) {
        this.allAccounts = [];
        this.allRoles = [];
        this.assignedAccounts = [];
        this.assignedAccountGroups = [];
    }

    public get assignedAccountIds(): number[] {
        return this.assignedAccounts.map(account => account.accountId);
    }

    public get assignedRoleLinkNames(): string[] {
        return this.assignedRoles.map(role => role.linkName);
    }

    public get assignedAccountGroupIds(): number[] {
        return this.assignedAccountGroups.map(accountGroup => accountGroup.accountGroupId);
    }

    public get assignedAccountsForTracking(): { accountId: number; name: string }[] {
        return this.assignedAccounts.map(({ accountId, name }) => ({
            accountId: accountId,
            name: name,
        }));
    }

    public get assignedRolesForTracking(): { name: string; linkName: string }[] {
        return this.assignedRoles.map(({ linkName, name }) => ({
            linkName: linkName,
            name: name,
        }));
    }

    public get assignedAccountGroupsForTracking(): { accountGroupId: number; name: string }[] {
        return this.assignedAccountGroups.map(({ accountGroupId, name }) => ({
            accountGroupId: accountGroupId,
            name: name,
        }));
    }

    private [_assignedAccountIds] = new Set<number>();
    private setInitialAssignedAccountIds(accounts: IAccountDetailsDTO[]): void {
        if (accounts) accounts.forEach(account => this[_assignedAccountIds].add(account.accountId));
    }
    private getAddedAccountIds(): number[] {
        return this.assignedAccountIds
            .filter(id => !this[_assignedAccountIds].has(id));
    }
    private getRemovedAccountIds(): number[] {
        return [...this[_assignedAccountIds]]
            .filter(id => !this.assignedAccountIds.includes(id));
    }

    private [_assignedRoleLinkNames] = new Set<string>();
    private setInitialAssignedRoleLinkNames(roles: IRolesDTO[]): void {
        if (roles) roles.forEach(role => this[_assignedRoleLinkNames].add(role.linkName));
    }
    private getAddedRoleLinkNames(): string[] {
        return this.assignedRoleLinkNames
            .filter(linkName => !this[_assignedRoleLinkNames].has(linkName));
    }
    private getRemovedRoleLinkNames(): string[] {
        return [...this[_assignedRoleLinkNames]]
            .filter(roleLinkedName => !this.assignedRoleLinkNames.includes(roleLinkedName));
    }

    private [_assignedAccountGroupIds] = new Set<number>();
    private setInitialAssignedAccountGroupIds(accountGroups: IAccountGroupDTO[]): void {
        if (accountGroups) accountGroups.forEach(accountGroup => this[_assignedAccountGroupIds].add(accountGroup.accountGroupId));
    }
    private getAddedAccountGroupIds(): number[] {
        return this.assignedAccountGroupIds
            .filter(id => !this[_assignedAccountGroupIds].has(id));
    }
    private getRemovedAccountGroupIds(): number[] {
        return [...this[_assignedAccountGroupIds]]
            .filter(id => !this.assignedAccountGroupIds.includes(id));
    }

    @mobx.observable
    public firstName: string;

    @mobx.observable
    public lastName: string;

    @mobx.observable
    public email: string;

    @mobx.observable
    public tempPassword: string;

    @mobx.observable
    public isSuperuser: boolean;

    @mobx.observable
    public isOpenExchangeStaffMember: boolean;

    @mobx.observable
    public isAllAccounts: boolean;

    @mobx.observable
    public username: string;

    @mobx.observable
    public assignedAccounts: IAccountDetailsDTO[];

    @mobx.observable
    public selectableAccounts: IAccountDetailsDTO[];

    @mobx.observable
    public allAccounts: IAccountDetailsDTO[];

    @mobx.observable
    public assignedRoles: IRolesDTO[];

    @mobx.observable
    public selectableRoles: IRolesDTO[];

    @mobx.observable
    public allRoles: IRolesDTO[];

    @mobx.observable
    public assignedAccountGroups: IAccountGroupDTO[];

    @mobx.observable
    public selectableAccountGroups: IAccountGroupDTO[];

    @mobx.observable
    public active: boolean;

    @mobx.observable
    public initialLockedSystemUser: boolean;

    @mobx.observable
    public lockedSystemUser: boolean;

    @mobx.observable
    public isFormChecked: boolean;

    @mobx.observable
    public serverErrorMessage: string;

    @mobx.observable
    public validationError: string[];

    @mobx.observable
    public isMailTouched: boolean;

    @mobx.observable
    public confirmationEmailPasswordTrigger: boolean;

    @mobx.action
    public setFirstName = (firstName: string) => {
        this.serverErrorMessage = '';
        this.firstName = firstName;
    };
    @mobx.action
    public setLastName = (lastName: string) => {
        this.serverErrorMessage = '';
        this.lastName = lastName;
    };
    @mobx.action
    public setEmail = (email: string) => {
        this.serverErrorMessage = '';
        this.email = email;
    };
    @mobx.action
    public setAssignedRoles = (roles: IRolesDTO[]) => {
        this.assignedRoles = roles;
    };
    @mobx.action
    public setAssignedAccounts = (accounts: IAccountDetailsDTO[]) => {
        this.assignedAccounts = accounts;
    };
    @mobx.action
    public setAssignedAccountGroups = (accountGroups: IAccountGroupDTO[]) => {
        this.assignedAccountGroups = accountGroups;
    };
    @mobx.action
    public generatePassword = () => {
        this.tempPassword = getRandomPassword();
    };
    @mobx.action
    public setSuperUser = (isSuperuser: boolean) => {
        this.isSuperuser = isSuperuser;
    };

    @mobx.action
    public toggleConfirmationEmailPassword = (): void => {
        this.confirmationEmailPasswordTrigger = !this.confirmationEmailPasswordTrigger;
    };

    @mobx.action
    public setIsOpenExchangeStaffMember = (isOpenExchangeStaffMember: boolean) => {
        this.isOpenExchangeStaffMember = isOpenExchangeStaffMember;
    };

    @mobx.action
    public setIsAllAccounts = (isAllAccounts: boolean) => {
        this.isAllAccounts = isAllAccounts;
    };

    @mobx.action
    public toggleActiveUser = (): void => {
        this.active = !this.active;
    };

    @mobx.action
    public toggleLockedSystemUser = (): void => {
        this.lockedSystemUser = !this.lockedSystemUser;
    };

    @mobx.action
    public initialize = (userDetails?: IUserDetailsDTO) => {
        this.assignedAccounts = userDetails?.accounts ? userDetails.accounts : this.defaultValues.accounts;
        this.setInitialAssignedAccountIds(userDetails?.accounts);
        this.assignedRoles = userDetails?.roles ? userDetails.roles : this.defaultValues.roles;
        this.setInitialAssignedRoleLinkNames(userDetails?.roles);
        this.assignedAccountGroups = userDetails?.accountGroups ? userDetails.accountGroups : this.defaultValues.accountGroups;
        this.setInitialAssignedAccountGroupIds(userDetails?.accountGroups);
        this.firstName = userDetails?.firstName || this.defaultValues.firstName;
        this.lastName = userDetails?.lastName || this.defaultValues.lastName;
        this.email = userDetails?.email || this.defaultValues.email;
        this.isSuperuser = userDetails?.isSuperuser !== undefined ? userDetails?.isSuperuser : this.defaultValues.isSuperuser;
        this.isOpenExchangeStaffMember = userDetails?.isOpenExchangeStaffMember !== undefined
            ? userDetails?.isOpenExchangeStaffMember : this.defaultValues.isOpenExchangeStaffMember;
        this.isAllAccounts = userDetails?.isAllAccounts !== undefined ? userDetails?.isAllAccounts : this.defaultValues.isAllAccounts;
        this.username = userDetails?.username;
        this.tempPassword = this.defaultValues.tempPassword;
        this.confirmationEmailPasswordTrigger = userDetails?.confirmationEmailPasswordTrigger !== undefined
            ? userDetails?.confirmationEmailPasswordTrigger
            : this.defaultValues.confirmationEmailPasswordTrigger;
        this.active = userDetails?.active !== undefined ? userDetails.active : this.defaultValues.active;
        this.initialLockedSystemUser = userDetails?.lockedSystemUser !== undefined ? userDetails.lockedSystemUser : this.defaultValues.lockedSystemUser;
        this.lockedSystemUser = userDetails?.lockedSystemUser !== undefined ? userDetails.lockedSystemUser : this.defaultValues.lockedSystemUser;
    };

    public getFirstNameError(intl: IntlShape): string | null {
        if (!this.firstName) {
            return intl.formatMessage({
                id: `oe.user.userFirstNameIsRequired`,
                defaultMessage: 'First Name is required',
                description: 'Error message in user popover'
            });
        } else if (this.firstName?.length > SHORT_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.firstName.tooLong`,
                defaultMessage: 'Too long - max length is 255 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    public getLastNameError(intl: IntlShape): string | null {
        if (!this.lastName) {
            return intl.formatMessage({
                id: `oe.user.userLastNameIsRequired`,
                defaultMessage: 'Last Name is required',
                description: 'Error message in user popover'
            });
        } else if (this.lastName.length > SHORT_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.lastName.tooLong`,
                defaultMessage: 'Too long - max length is 255 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    public isMailInValid(intl: IntlShape): boolean {
        return !!this.getEmailError(intl);
    }

    public shouldShowEmailValidationError(intl: IntlShape): boolean {
        return this.isMailInValid(intl) && this.isMailTouched || this.showValidationErrors();
    }

    public getEmailError(intl: IntlShape): string | null {
        if (this.email) {
            return EMAIL_VALIDATION_REG_EXP.test(this.email) ? null
                : intl.formatMessage({
                    id: `oe.email.emailIsNotValid`,
                    defaultMessage: 'Must be a valid email address',
                    description: 'Error message in user popover'
                });
        }
        return intl.formatMessage({
            id: `oe.email.emailIsRequired`,
            defaultMessage: 'Email is required',
            description: 'Error message in user popover'
        });
    }

    public getPasswordError(intl: IntlShape): string | null {
        if (!this.username && !this.tempPassword) {
            return intl.formatMessage({
                id: `oe.password.passwordIsRequired`,
                defaultMessage: 'Password is required, please generate it',
                description: 'Error message in user popover'
            });
        }

        return null;
    }

    public isFormValid(intl: IntlShape): boolean {
        return !this.getFirstNameError(intl)
            && !this.getLastNameError(intl)
            && !this.getEmailError(intl)
            && !this.getPasswordError(intl);
    }

    @mobx.action
    public allowToShowValidationErrors(): void {
        this.isFormChecked = true;
    }

    public showValidationErrors(): boolean {
        return this.isFormChecked;
    }

    public toApiCreateUserDTO(): IUserCreateDTO {
        return {
            accountIds: this.assignedAccounts.map(account => account.accountId),
            roleLinkNames: this.assignedRoles.map(role => role.linkName),
            accountGroupIds: this.assignedAccountGroups.map(accountGroup => accountGroup.accountGroupId),
            email: this.email.trim(),
            firstName: this.firstName.trim(),
            lastName: this.lastName.trim(),
            isSuperuser: this.isSuperuser,
            isOpenExchangeStaffMember: this.isOpenExchangeStaffMember,
            isAllAccounts: this.isAllAccounts,
            tempPassword: this.tempPassword,
            confirmationEmailPasswordTrigger: this.confirmationEmailPasswordTrigger,
        };
    }

    public toApiUpdateUserDTO(): IUserUpdateDTO {
        const result: IUserUpdateDTO = {
            addedAccounts: this.getAddedAccountIds(),
            removedAccounts: this.getRemovedAccountIds(),
            addedRoles: this.getAddedRoleLinkNames(),
            removedRoles: this.getRemovedRoleLinkNames(),
            addedAccountGroups: this.getAddedAccountGroupIds(),
            removedAccountGroups: this.getRemovedAccountGroupIds(),
            email: this.email.trim(),
            firstName: this.firstName.trim(),
            lastName: this.lastName.trim(),
            isSuperuser: this.isSuperuser,
            isOpenExchangeStaffMember: this.isOpenExchangeStaffMember,
            isAllAccounts: this.isAllAccounts,
            confirmationEmailPasswordTrigger: this.confirmationEmailPasswordTrigger,
            lockedSystemUser: this.lockedSystemUser
        };

        if (this.tempPassword) {
            result.tempPassword = this.tempPassword;
        }

        return result;
    }

    public fetchAccountsList = async (query?: string): Promise<void> => {
        const response = await this.accountsDAO.getAccounts({
            page: 0,
            size: COMMON_ITEMS_PER_PAGE,
            query: query?.trim() || ''
        });
        mobx.runInAction(() => {
            this.selectableAccounts = response.content;
        });
    };

    public fetchRolesList = async (): Promise<void> => {
        const response = await this.rolesDAO.getAllRoles();
        mobx.runInAction(() => {
            this.allRoles = response;
            this.selectableRoles = response;
        });
    };

    public fetchAccountGroups = async (query?: string): Promise<void> => {
        const response = await this.accountsGroupsDAO.getAccountGroups({
            page: 0,
            size: COMMON_ITEMS_PER_PAGE,
            query: query?.trim() || ''
        });
        mobx.runInAction(() => {
            this.selectableAccountGroups = response.content;
        });
    };

    @mobx.action
    public removeRole = (linkName: string) => {
        this.assignedRoles = this.assignedRoles.filter(role => role.linkName !== linkName);
    };

    @mobx.action
    public removeAssignedAccountGroups = (accountGroupId: number) => {
        this.assignedAccountGroups = this.assignedAccountGroups.filter(accountGroup => accountGroup.accountGroupId !== accountGroupId);
    };

    @mobx.action
    public removeAssignedAccount = (accountId: number) => {
        this.assignedAccounts = this.assignedAccounts.filter(account => account.accountId !== accountId);
    };

    @mobx.action
    public searchAccounts = (query: string): void => {
        if (!query || query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.fetchAccountsList(query);
        }
    };

    @mobx.action
    public searchRoles = (query: string): void => {
        if (this.allRoles.length) {
            if (!query || query.trim().length >= MINIMAL_SEARCH_LENGTH) {
                this.selectableRoles = this.allRoles.filter(role => {
                    const roleName = role.name.toLocaleLowerCase();
                    return roleName.includes(query?.toLocaleLowerCase() || '');
                }
                );
            }
        }
    };

    @mobx.action
    public searchAccountGroups = (query: string): void => {
        if (!query || query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.fetchAccountGroups(query);
        }
    };
}

export { UserFormDataVM };
